import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Typography,
  Space,
  Button,
  Tag,
  Spin,
  Alert,
  Breadcrumb,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  PlanService,
  type Plan,
  type PlanTenant,
  type PlanTenantsResponse,
} from '../../services/plan.service';

const { Title, Text } = Typography;

const STATUS_COLORS: Record<PlanTenant['status'], string> = {
  trial: 'gold',
  active: 'green',
  grace: 'orange',
  suspended: 'volcano',
  cancelled: 'default',
};

const STATUS_LABELS: Record<PlanTenant['status'], string> = {
  trial: 'Dùng thử',
  active: 'Đang dùng',
  grace: 'Gia hạn',
  suspended: 'Tạm ngưng',
  cancelled: 'Đã hủy',
};

export const PlanTenants: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [plan, setPlan] = useState<Plan | null>(null);
  const [response, setResponse] = useState<PlanTenantsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [planData, tenantsData] = await Promise.all([
        PlanService.getPlanById(id),
        PlanService.getTenantsByPlan(id, page, limit),
      ]);
      setPlan(planData);
      setResponse(tenantsData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Tải dữ liệu thất bại'));
    } finally {
      setLoading(false);
    }
  }, [id, page, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      title: 'Tên tenant',
      dataIndex: ['tenant', 'name'],
      key: 'tenant_name',
      render: (_: unknown, record: PlanTenant) => {
        const tenantId = record.tenant?._id;
        const tenantName = (record.tenant?.name as string | undefined) ?? '—';
        return tenantId ? (
          <Link to={`/tenants/${tenantId}`}>{tenantName}</Link>
        ) : (
          <Text type="secondary">{tenantName}</Text>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: ['tenant', 'email'],
      key: 'email',
      render: (_: unknown, record: PlanTenant) =>
        (record.tenant?.email as string | undefined) ?? '—',
    },
    {
      title: 'Chu kỳ',
      dataIndex: 'billing_cycle',
      key: 'billing_cycle',
      render: (cycle: PlanTenant['billing_cycle']) => (
        <Tag color={cycle === 'yearly' ? 'blue' : 'cyan'}>
          {cycle === 'yearly' ? 'Năm' : 'Tháng'}
        </Tag>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status: PlanTenant['status']) => (
        <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
      ),
    },
    {
      title: 'Hết hạn kỳ hiện tại',
      dataIndex: 'current_period_end',
      key: 'current_period_end',
      render: (date: string) =>
        date ? dayjs(date).format('DD/MM/YYYY') : '—',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb
        items={[
          { title: <Link to="/plans">Gói cước</Link> },
          { title: plan?.display_name ?? 'Chi tiết' },
          { title: 'Tenant đang dùng' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Space style={{ marginBottom: 24 }} align="center">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/plans')}>
          Quay lại
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {plan ? `Tenant đang dùng gói ${plan.display_name}` : 'Tenant theo gói'}
        </Title>
        <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading} />
      </Space>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Tải tenant thất bại"
          description={error.message}
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading}>
        <Table<PlanTenant>
          rowKey="subscription_id"
          dataSource={response?.data ?? []}
          columns={columns}
          pagination={{
            current: page,
            pageSize: limit,
            total: response?.meta.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (newPage, newLimit) => {
              setPage(newPage);
              setLimit(newLimit);
            },
            showTotal: (total) => `Tổng ${total} tenant`,
          }}
        />
      </Spin>
    </div>
  );
};

export default PlanTenants;
